/** Global popup dialogs. */

const DIALOG_SELECTOR = 'dialog[data-dialog]';
const TRIGGER_SELECTOR = '[data-dialog-trigger][aria-controls]';
const CLOSE_SELECTOR = '[data-dialog-close]';
const SCROLL_LOCK_ATTRIBUTE = 'data-dialog-open';
const INLINE_ATTRIBUTE = 'data-dialog-inline';
const GUARDED_GESTURES = [
  'wheel',
  'touchstart',
  'touchmove',
  'touchend',
  'touchcancel',
  'pointerdown',
  'pointermove',
  'pointerup',
  'pointercancel',
  'mousedown',
  'mousemove',
  'mouseup',
];
const CANCELLED_GESTURES = new Set(['wheel', 'touchmove']);
const TEXT_ENTRY_SELECTOR =
  'input:not([type="button"], [type="checkbox"], [type="color"], [type="file"], [type="image"], [type="radio"], [type="range"], [type="reset"], [type="submit"]), textarea, [contenteditable]:not([contenteditable="false"])';
const SCROLLER_SELECTOR = '.g-dialog__body, [data-dialog-scroller]';
const FADE_ATTRIBUTE = 'data-dialog-fade';
const OPENING_ATTRIBUTE = 'data-dialog-opening';
const CLOSING_ATTRIBUTE = 'data-dialog-closing';
const SCROLL_STOP_ATTRIBUTE = 'data-dialog-scroll-stop';
const DIALOG_LINK_SELECTOR = 'a[role="button"][aria-haspopup="dialog"]';

const openerByDialog = new WeakMap();
const skipFocusRestore = new WeakSet();
const landingByDialog = new WeakMap();
const closingTokens = new WeakMap();
const fadeWatchers = new Map();

let gestureGuardActive = false;
let gestureStartedInDialog = false;
let cachedDialogRects = null;
let touchGesture = null;
let pressedDialogLink = null;

function isManagedDialog(node) {
  return node instanceof HTMLDialogElement && node.matches(DIALOG_SELECTOR);
}

/**
 * Whether the theme editor is rendering this document.
 *
 * @returns {boolean} True in Shopify design mode.
 */
function isDesignMode() {
  return Boolean(window.Shopify && window.Shopify.designMode);
}

/**
 * Derive the lock from the DOM rather than a counter, so a dialog removed while
 * open — a replaced section, a discarded editor block — cannot strand the lock.
 */
function updateScrollLock() {
  const hasOpenDialog = document.querySelector(`${DIALOG_SELECTOR}[open]`) !== null;
  document.documentElement.toggleAttribute(SCROLL_LOCK_ATTRIBUTE, hasOpenDialog && !isDesignMode());
  syncGestureGuard();
}

/** Rects a gesture may act on: every open dialog's box and its top-level content. */
function openDialogRects() {
  if (cachedDialogRects) return cachedDialogRects;

  const rects = [];
  document.querySelectorAll('dialog[open]').forEach((dialog) => {
    rects.push(...dialog.getClientRects());
    for (const child of dialog.children) rects.push(...child.getClientRects());
  });

  cachedDialogRects = rects;
  requestAnimationFrame(() => {
    cachedDialogRects = null;
  });
  return rects;
}

function isPointInOpenDialog(x, y) {
  return openDialogRects().some(
    (rect) => x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
  );
}

/** Whether a gesture belongs to a popup, latched at the press so a drag is never cut in half. */
function isGestureAllowed(event) {
  switch (event.type) {
    case 'touchstart': {
      const touch = event.touches[0] || event.changedTouches[0];
      gestureStartedInDialog = touch
        ? isPointInOpenDialog(touch.clientX, touch.clientY)
        : false;
      return gestureStartedInDialog;
    }
    case 'touchmove':
    case 'touchend':
    case 'touchcancel':
      return gestureStartedInDialog;
    case 'pointerdown':
    case 'mousedown':
      gestureStartedInDialog = isPointInOpenDialog(event.clientX, event.clientY);
      return gestureStartedInDialog;
    case 'pointermove':
    case 'mousemove':
      return event.buttons
        ? gestureStartedInDialog
        : isPointInOpenDialog(event.clientX, event.clientY);
    case 'pointerup':
    case 'pointercancel':
    case 'mouseup':
      return gestureStartedInDialog || isPointInOpenDialog(event.clientX, event.clientY);
    default:
      return isPointInOpenDialog(event.clientX, event.clientY);
  }
}

/**
 * Whether a native scroller can move the way a drag is heading.
 *
 * @param {Element} element - Candidate scroller.
 * @param {boolean} vertical - Whether the drag is mostly vertical.
 * @param {number} delta - Finger travel along that axis since the touch began.
 * @returns {boolean} True when the element would consume the drag.
 */
function canScrollToward(element, vertical, delta) {
  const style = getComputedStyle(element);
  const overflow = vertical ? style.overflowY : style.overflowX;
  if (overflow !== 'auto' && overflow !== 'scroll') return false;

  const position = vertical ? element.scrollTop : element.scrollLeft;
  const max = vertical
    ? element.scrollHeight - element.clientHeight
    : element.scrollWidth - element.clientWidth;
  if (max < 1) return false;

  return delta > 0 ? position > 0 : position < max - 1;
}

/**
 * Whether a drag that starts in a popup stays in one of its own scrollers.
 *
 * @param {Element} target - Element the touch started on.
 * @param {number} deltaX - Horizontal finger travel since the touch began.
 * @param {number} deltaY - Vertical finger travel since the touch began.
 * @returns {boolean} True when a scroller inside the popup takes the drag.
 */
function canScrollWithinDialog(target, deltaX, deltaY) {
  const vertical = Math.abs(deltaY) >= Math.abs(deltaX);
  const delta = vertical ? deltaY : deltaX;

  for (let node = target; node instanceof Element; node = node.parentElement) {
    if (canScrollToward(node, vertical, delta)) return true;
    if (node instanceof HTMLDialogElement) return false;
  }
  return false;
}

/** Whether the visitor must be able to pan the page: it is pinch-zoomed, or a popup field has the keyboard. */
function needsViewportPanning() {
  if (window.visualViewport && window.visualViewport.scale > 1) return true;

  const active = document.activeElement;
  return (
    active instanceof HTMLElement &&
    active.matches(TEXT_ENTRY_SELECTOR) &&
    active.closest('dialog[open]') !== null
  );
}

/** Cancel every move of a drag inside a popup that no popup scroller can take, so it cannot scroll the page. */
function containTouchScroll(event) {
  if (!touchGesture || event.touches.length > 1) return;

  if (touchGesture.contained === null) {
    const touch = event.touches[0];
    const deltaX = touch.clientX - touchGesture.x;
    const deltaY = touch.clientY - touchGesture.y;
    if (deltaX === 0 && deltaY === 0) return;

    touchGesture.contained =
      !needsViewportPanning() && !canScrollWithinDialog(event.target, deltaX, deltaY);
  }

  if (touchGesture.contained && event.cancelable) event.preventDefault();
}

function trackTouchStart(event) {
  if (event.touches.length !== 1) return;
  const touch = event.touches[0];
  touchGesture = { x: touch.clientX, y: touch.clientY, contained: null };
}

function handleGuardedGesture(event) {
  if (event.type === 'touchstart') trackTouchStart(event);

  if (isGestureAllowed(event)) {
    if (event.type === 'touchmove') containTouchScroll(event);
    return;
  }

  // Cancelling the default only stops native scrolling; a carousel translates
  // slides from its own listener, so the event must not reach the page at all.
  event.stopPropagation();
  if (CANCELLED_GESTURES.has(event.type) && event.cancelable) event.preventDefault();
}

/** Withhold every pointer, touch and wheel gesture aimed outside the open popups. */
function setGestureGuard(active) {
  if (active === gestureGuardActive) return;
  gestureGuardActive = active;
  cachedDialogRects = null;
  gestureStartedInDialog = false;
  touchGesture = null;

  if (!active) {
    GUARDED_GESTURES.forEach((type) => {
      document.removeEventListener(type, handleGuardedGesture, true);
    });
    return;
  }

  GUARDED_GESTURES.forEach((type) => {
    document.addEventListener(type, handleGuardedGesture, { capture: true, passive: false });
  });
}

function syncGestureGuard() {
  setGestureGuard(!isDesignMode() && document.querySelector('dialog[open]') !== null);
}

function syncTriggers(dialog, expanded) {
  if (!dialog.id) return;
  const controls = CSS.escape(dialog.id);
  document
    .querySelectorAll(
      `[data-dialog-trigger][aria-controls="${controls}"], a[aria-haspopup="dialog"][aria-controls="${controls}"]`
    )
    .forEach((trigger) => trigger.setAttribute('aria-expanded', String(expanded)));
}

/**
 * Return a reused popup to the top, so the scroll position its previous content
 * was left at is not what the next open shows.
 *
 * @param {HTMLDialogElement} dialog - A dialog that has just been shown.
 * @returns {void}
 */
export function scrollToTop(dialog) {
  if (dialog.querySelector('[autofocus]')) return;

  const options = { top: 0, behavior: 'instant' };
  dialog.scrollTo(options);
  dialog.querySelectorAll(SCROLLER_SELECTOR).forEach((scroller) => scroller.scrollTo(options));
}

function focusInitialTarget(dialog) {
  // Opening already honors an explicit autofocus target.
  if (dialog.querySelector('[autofocus]')) return;
  const closeControl = dialog.querySelector(CLOSE_SELECTOR);
  if (closeControl instanceof HTMLElement) closeControl.focus({ preventScroll: true });
}

/**
 * Which edges of a scroller still hide content.
 *
 * @param {Element} scroller - An element whose computed `overflow-y` scrolls.
 * @returns {{start: boolean, end: boolean}} Whether content is hidden above and below.
 */
function readFadeEdges(scroller) {
  const room = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
  const offset = Math.min(room, Math.max(0, scroller.scrollTop));
  return { start: offset > 1, end: room - offset > 1 };
}

function writeFadeEdges(scroller, { start, end }) {
  scroller.toggleAttribute('data-fade-start', start);
  scroller.toggleAttribute('data-fade-end', end);
}

/**
 * Make an overflowing popup scroller with nothing focusable inside a named keyboard stop, or undo it.
 *
 * @param {HTMLDialogElement} dialog - Dialog whose accessible name the stop borrows.
 * @param {Element} scroller - A popup scroller.
 * @param {boolean} needed - Whether the scroller overflows and holds no focusable element.
 * @param {boolean} [force] - Remove the stop even while it has focus.
 * @returns {void}
 */
function syncScrollStop(dialog, scroller, needed, force = false) {
  const added = scroller.hasAttribute(SCROLL_STOP_ATTRIBUTE);

  if (needed) {
    if (added || scroller.hasAttribute('tabindex')) return;
    const labelledBy = dialog.getAttribute('aria-labelledby');
    const label = dialog.getAttribute('aria-label');
    if (!labelledBy && !label) return;

    scroller.setAttribute('tabindex', '0');
    scroller.setAttribute('role', 'region');
    if (labelledBy) scroller.setAttribute('aria-labelledby', labelledBy);
    else scroller.setAttribute('aria-label', label);
    scroller.setAttribute(SCROLL_STOP_ATTRIBUTE, '');
    return;
  }

  if (!added || (!force && document.activeElement === scroller)) return;
  ['tabindex', 'role', 'aria-labelledby', 'aria-label', SCROLL_STOP_ATTRIBUTE].forEach((name) =>
    scroller.removeAttribute(name)
  );
}

/**
 * Fade an open popup's scrolling content out at whichever edge still hides content.
 *
 * @param {HTMLDialogElement} dialog - The dialog that just opened.
 * @returns {() => void} Stops watching; the fade attributes stay for the closing transition.
 */
function watchScrollFades(dialog) {
  const frame = window.DieuxFrame;
  if (!frame) return () => {};

  const scanKey = Symbol('dialog-fade-scan');
  const edgeKeys = new Map();

  const updateEdges = (event) => {
    const scroller = event.currentTarget;
    frame.measure(edgeKeys.get(scroller), () => {
      const edges = readFadeEdges(scroller);
      frame.write(() => writeFadeEdges(scroller, edges));
    });
  };

  const scan = () => {
    frame.measure(scanKey, () => {
      const edgesByScroller = new Map();
      for (const candidate of dialog.querySelectorAll(SCROLLER_SELECTOR)) {
        const { overflowY } = window.getComputedStyle(candidate);
        if (overflowY === 'auto' || overflowY === 'scroll') {
          edgesByScroller.set(candidate, {
            ...readFadeEdges(candidate),
            needsStop:
              candidate.scrollHeight > candidate.clientHeight + 1 &&
              !frame.hasTabbable?.(candidate),
          });
        }
      }
      frame.write(() => applyScan(edgesByScroller));
    });
  };

  const resizeObserver = new ResizeObserver(scan);
  const mutationObserver = new MutationObserver(scan);

  const release = (scroller) => {
    if (edgeKeys.has(scroller)) {
      scroller.removeEventListener('scroll', updateEdges);
      resizeObserver.unobserve(scroller);
      frame.cancelMeasure(edgeKeys.get(scroller));
      edgeKeys.delete(scroller);
    }
    scroller.removeAttribute(FADE_ATTRIBUTE);
    writeFadeEdges(scroller, { start: false, end: false });
    syncScrollStop(dialog, scroller, false);
  };

  const applyScan = (edgesByScroller) => {
    for (const scroller of [...edgeKeys.keys(), ...dialog.querySelectorAll(`[${FADE_ATTRIBUTE}]`)]) {
      if (!edgesByScroller.has(scroller)) release(scroller);
    }
    for (const [scroller, edges] of edgesByScroller) {
      if (!edgeKeys.has(scroller)) {
        edgeKeys.set(scroller, Symbol('dialog-fade-edges'));
        scroller.setAttribute(FADE_ATTRIBUTE, '');
        scroller.addEventListener('scroll', updateEdges, { passive: true });
        resizeObserver.observe(scroller);
        frame.write(() => {
          if (edgeKeys.has(scroller)) scroller.setAttribute(FADE_ATTRIBUTE, 'live');
        });
      }
      // A child resizing is the only signal that the scroll height changed.
      for (const child of scroller.children) resizeObserver.observe(child);
      writeFadeEdges(scroller, edges);
      syncScrollStop(dialog, scroller, edges.needsStop);
    }
  };

  mutationObserver.observe(dialog, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['hidden', 'inert', 'open', 'disabled', 'tabindex', 'href', 'controls', 'contenteditable'],
  });
  scan();

  return () => {
    mutationObserver.disconnect();
    resizeObserver.disconnect();
    frame.cancelMeasure(scanKey);
    for (const [scroller, key] of edgeKeys) {
      scroller.removeEventListener('scroll', updateEdges);
      frame.cancelMeasure(key);
    }
    edgeKeys.clear();
    dialog
      .querySelectorAll(`[${SCROLL_STOP_ATTRIBUTE}]`)
      .forEach((scroller) => syncScrollStop(dialog, scroller, false, true));
  };
}

function stopScrollFades(dialog) {
  fadeWatchers.get(dialog)?.();
  fadeWatchers.delete(dialog);
}

function releaseDetachedFades() {
  for (const dialog of fadeWatchers.keys()) {
    if (!dialog.isConnected) stopScrollFades(dialog);
  }
}

function handleEditorUnload() {
  updateScrollLock();
  setTimeout(releaseDetachedFades);
}

/**
 * Open a managed dialog.
 *
 * @param {HTMLDialogElement} dialog - The `<dialog data-dialog>` to open.
 * @param {{opener?: Element}} [options] - `opener` receives focus again on close.
 * @returns {boolean} Whether this call opened the dialog.
 */
export function openDialog(dialog, { opener } = {}) {
  if (!isManagedDialog(dialog) || !dialog.isConnected) return false;
  if (dialog.open) {
    const reversed = cancelClosing(dialog, opener);
    if (reversed) scrollToTop(dialog);
    return reversed;
  }

  if (opener instanceof HTMLElement) openerByDialog.set(dialog, opener);
  else openerByDialog.delete(dialog);
  skipFocusRestore.delete(dialog);
  landingByDialog.delete(dialog);

  dialog.setAttribute(OPENING_ATTRIBUTE, '');
  if (isDesignMode()) {
    dialog.setAttribute(INLINE_ATTRIBUTE, '');
    dialog.show();
  } else {
    dialog.showModal();
  }

  // A scroll write does not stick on a `display: none` element, so this follows the show.
  scrollToTop(dialog);

  updateScrollLock();
  syncTriggers(dialog, true);
  focusInitialTarget(dialog);
  stopScrollFades(dialog);
  fadeWatchers.set(dialog, watchScrollFades(dialog));
  releaseOpening(dialog);

  dialog.dispatchEvent(new CustomEvent('dieux:dialog:open', { bubbles: true }));
  return true;
}

/**
 * Start the entrance one frame after the dialog is first laid out and painted.
 *
 * @param {HTMLDialogElement} dialog - A dialog that has just been shown at its hidden values.
 */
function releaseOpening(dialog) {
  const release = () => dialog.removeAttribute(OPENING_ATTRIBUTE);
  const frame = window.DieuxFrame;
  if (!frame) {
    release();
    return;
  }
  frame.write(() => frame.write(release));
}

/**
 * Close a managed dialog.
 *
 * @param {HTMLDialogElement} dialog - The `<dialog data-dialog>` to close.
 * @param {{restoreFocus?: boolean, landing?: HTMLElement}} [options] - Set `restoreFocus`
 *   false when the opener is going away, so focus is not pulled to a detached element.
 *   `landing` replaces the opener as the element focus moves to once the exit has finished.
 * @returns {boolean} Whether this call closed the dialog.
 */
export function closeDialog(dialog, { restoreFocus = true, landing } = {}) {
  if (!isManagedDialog(dialog) || !dialog.open) return false;
  if (!restoreFocus) skipFocusRestore.add(dialog);
  if (landing instanceof HTMLElement) landingByDialog.set(dialog, landing);
  if (dialog.hasAttribute(CLOSING_ATTRIBUTE)) return true;

  // Safari has no `overlay`, so the exit runs while the dialog is still in the top layer.
  dialog.setAttribute(CLOSING_ATTRIBUTE, '');
  const exits = dialog.getAnimations().map((animation) => animation.finished);
  if (exits.length === 0) {
    finishClosing(dialog);
    return true;
  }

  const token = Symbol('dialog-closing');
  closingTokens.set(dialog, token);
  Promise.allSettled(exits).then(() => {
    if (closingTokens.get(dialog) === token) finishClosing(dialog);
  });
  return true;
}

function finishClosing(dialog) {
  closingTokens.delete(dialog);
  dialog.removeAttribute(CLOSING_ATTRIBUTE);
  if (dialog.open) dialog.close();
}

/**
 * Reverse an exit that is still running, as when a trigger is pressed again mid-fade.
 *
 * @param {HTMLDialogElement} dialog - An open managed dialog.
 * @param {Element} [opener] - Replaces the element that receives focus on close.
 * @returns {boolean} Whether an exit was reversed.
 */
function cancelClosing(dialog, opener) {
  if (!dialog.hasAttribute(CLOSING_ATTRIBUTE)) return false;
  closingTokens.delete(dialog);
  dialog.removeAttribute(CLOSING_ATTRIBUTE);
  skipFocusRestore.delete(dialog);
  landingByDialog.delete(dialog);
  if (opener instanceof HTMLElement) openerByDialog.set(dialog, opener);
  return true;
}

function handleCancel(event) {
  if (!isManagedDialog(event.target)) return;
  event.preventDefault();
  closeDialog(event.target);
}

/**
 * The managed dialog a same-document fragment names.
 *
 * @param {string} hash - Fragment, including the leading `#`.
 * @returns {HTMLDialogElement|null} Dialog the fragment names, or `null` when it names something else.
 */
function dialogFromHash(hash) {
  if (!hash || hash.length < 2) return null;

  let id;
  try {
    id = decodeURIComponent(hash.slice(1));
  } catch {
    id = hash.slice(1);
  }

  const dialog = document.getElementById(id);
  return isManagedDialog(dialog) ? dialog : null;
}

/**
 * The managed dialog a link opens instead of navigating to.
 *
 * @param {Element} link - Candidate anchor element.
 * @returns {HTMLDialogElement|null} Dialog the link names, or `null` when the link leaves this document.
 */
function dialogFromLink(link) {
  if (!(link instanceof HTMLAnchorElement)) return null;

  let url;
  try {
    url = new URL(link.href, window.location.href);
  } catch {
    return null;
  }

  if (url.origin !== window.location.origin) return null;
  if (url.pathname !== window.location.pathname) return null;
  if (url.search !== window.location.search) return null;

  return dialogFromHash(url.hash);
}

function handleClick(event) {
  if (!(event.target instanceof Element)) return;

  const closeControl = event.target.closest(CLOSE_SELECTOR);
  if (closeControl) {
    const dialog = closeControl.closest(DIALOG_SELECTOR);
    if (isManagedDialog(dialog)) {
      event.preventDefault();
      closeDialog(dialog);
      return;
    }
  }

  const trigger = event.target.closest(TRIGGER_SELECTOR);
  if (trigger) {
    const dialog = document.getElementById(trigger.getAttribute('aria-controls'));
    if (isManagedDialog(dialog)) {
      event.preventDefault();
      openDialog(dialog, { opener: trigger });
    }
    return;
  }

  const isPlainActivation =
    event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
  if (isPlainActivation) {
    const link = event.target.closest('a[href*="#"]');
    const linkedDialog = dialogFromLink(link);
    if (linkedDialog) {
      event.preventDefault();
      openDialog(linkedDialog, { opener: link });
      return;
    }
  }

  // A click on `::backdrop` retargets to the dialog element itself.
  if (isManagedDialog(event.target) && event.target.open) closeDialog(event.target);
}

/**
 * Move focus to a replacement landing once a popup has closed.
 *
 * @param {HTMLElement} landing - Element focus moves to.
 * @returns {void}
 */
function focusLanding(landing) {
  const anchorScroll = window.DieuxAnchorScroll;
  if (typeof anchorScroll?.focusLanding === 'function') anchorScroll.focusLanding(landing);
  else landing.focus({ preventScroll: true });
}

/**
 * Expose same-document links that open a popup instead of navigating as popup triggers.
 *
 * @returns {void}
 */
function annotateDialogLinks() {
  document.querySelectorAll('a[href*="#"]').forEach((link) => {
    const dialog = dialogFromLink(link);
    if (!dialog || !dialog.id) return;
    link.setAttribute('role', 'button');
    link.setAttribute('aria-haspopup', 'dialog');
    link.setAttribute('aria-controls', dialog.id);
    link.setAttribute('aria-expanded', String(dialog.open));
  });
}

/**
 * The popup link a plain Space press is aimed at.
 *
 * @param {KeyboardEvent} event - Space keydown or keyup.
 * @returns {HTMLAnchorElement|null} The focused popup link, or `null` for any other key or target.
 */
function dialogLinkFromSpace(event) {
  if (event.key !== ' ' && event.key !== 'Spacebar') return null;
  if (event.altKey || event.ctrlKey || event.metaKey) return null;
  if (!(event.target instanceof Element)) return null;
  const link = event.target.closest(DIALOG_LINK_SELECTOR);
  return link instanceof HTMLAnchorElement ? link : null;
}

function handleKeydown(event) {
  if (event.defaultPrevented) return;
  const link = dialogLinkFromSpace(event);
  if (!link) return;
  event.preventDefault();
  if (!event.repeat) pressedDialogLink = link;
}

function handleKeyup(event) {
  const pressed = pressedDialogLink;
  pressedDialogLink = null;
  const link = dialogLinkFromSpace(event);
  if (!link || link !== pressed) return;
  event.preventDefault();
  link.click();
}

/**
 * Re-annotate popup links whenever a whole section is swapped in, as deferred sections are.
 *
 * @returns {void}
 */
function watchSectionSwaps() {
  const observer = new MutationObserver((records) => {
    if (records.some((record) => [...record.addedNodes].some((node) => node instanceof Element))) {
      annotateDialogLinks();
    }
  });
  const parents = new Set();
  document.querySelectorAll('.shopify-section').forEach((section) => {
    if (section.parentElement) parents.add(section.parentElement);
  });
  parents.forEach((parent) => observer.observe(parent, { childList: true }));
}

function handleClose(event) {
  const dialog = event.target;
  if (!isManagedDialog(dialog)) return;

  closingTokens.delete(dialog);
  dialog.removeAttribute(OPENING_ATTRIBUTE);
  dialog.removeAttribute(CLOSING_ATTRIBUTE);
  dialog.removeAttribute(INLINE_ATTRIBUTE);
  updateScrollLock();
  syncTriggers(dialog, false);
  stopScrollFades(dialog);

  const opener = openerByDialog.get(dialog);
  openerByDialog.delete(dialog);
  const landing = landingByDialog.get(dialog);
  landingByDialog.delete(dialog);
  const restoreFocus = !skipFocusRestore.has(dialog);
  skipFocusRestore.delete(dialog);

  if (landing instanceof HTMLElement && landing.isConnected) {
    focusLanding(landing);
    return;
  }

  if (restoreFocus && opener instanceof HTMLElement && opener.isConnected) {
    opener.focus({ preventScroll: true });
  }
}

function followInitialFragment() {
  const dialog = dialogFromHash(window.location.hash);
  if (!dialog || !openDialog(dialog)) return;
  const main = document.getElementById('MainContent');
  if (main) landingByDialog.set(dialog, main);
}

if (!window.DieuxDialog) {
  document.addEventListener('click', handleClick);
  document.addEventListener('keydown', handleKeydown);
  document.addEventListener('keyup', handleKeyup);
  document.addEventListener('close', handleClose, true);
  document.addEventListener('cancel', handleCancel, true);
  window.addEventListener('hashchange', followInitialFragment);

  // A section or block torn out while its dialog is open never fires `close`.
  document.addEventListener('shopify:section:unload', handleEditorUnload);
  document.addEventListener('shopify:block:unload', handleEditorUnload);
  document.addEventListener('shopify:section:load', annotateDialogLinks);

  // Drawers and lightboxes this controller does not own still open a dialog.
  new MutationObserver(syncGestureGuard).observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ['open'],
  });
  syncGestureGuard();
  annotateDialogLinks();
  watchSectionSwaps();
  followInitialFragment();

  // Owners rendered inside a classic `{% javascript %}` tag cannot import.
  window.DieuxDialog = { openDialog, closeDialog, scrollToTop };
}
